import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Layout from './components/layout/Layout';
import Home from './pages/Home';
import React, { useEffect, lazy, Suspense } from 'react';
import initializeFirestore from './utils/initializeFirestore';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from './components/layout/Navbar';

import EditData from './components/admin/EditData';
import ContactSubmissions from './components/admin/ContactSubmissions';
import CateringSubmissions from './components/admin/CateringSubmissions';
import Login from './pages/Login';
import SiteDataProvider from './context/SiteDataProvider';

import Dashboard from './pages/admin/Dashboard';
import AuthProvider from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminLayout from './components/layout/AdminLayout';
import Unauthorized from './pages/Unauthorized';
import BCCEmailManagement from './pages/admin/BCCEmailManagement';
import DataBackup from './components/admin/DataBackup';
import EditBusinessInfo from './pages/admin/EditBusinessInfo';
import EditHomeHero from './pages/admin/EditHomeHero';
import EditHomeReviews from './pages/admin/EditHomeReviews';
import EditHomeGallery from './pages/admin/EditHomeGallery';
import EditHomeStats from './pages/admin/EditHomeStats';
import EditAbout from './pages/admin/EditAbout';
import EditCatering from './pages/admin/EditCatering';
import EditNotices from './pages/admin/EditNotices';
import FullScreenLoader from './components/ui/FullScreenLoader';
import EditServices from './pages/admin/EditServices';
import Orders from './pages/admin/Orders';
import OrderDetails from './pages/admin/OrderDetails';
import SimpleMenu from './pages/SimpleMenu';

const queryClient = new QueryClient();

// Lazy load pages
const Menu = lazy(() => import('./pages/Menu'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Catering = lazy(() => import('./pages/Catering'));

function App() {
  useEffect(() => {
    // Initialize Firestore (run only once)
    initializeFirestore();
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SiteDataProvider>
            <Router>
              <Suspense fallback={<FullScreenLoader />}>
                <Routes>
                  {/* Public Routes */}
                  <Route element={<Layout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/menu" element={
                      <Suspense fallback={<FullScreenLoader />}>
                        {/* <Menu /> */}
                        <SimpleMenu/>
                      </Suspense>
                    } />
                    <Route path="/about" element={
                      <Suspense fallback={<FullScreenLoader />}>
                        <About />
                      </Suspense>
                    } />
                    <Route path="/contact" element={
                      <Suspense fallback={<FullScreenLoader />}>
                        <Contact />
                      </Suspense>
                    } />
                    <Route path="/catering" element={
                      <Suspense fallback={<FullScreenLoader />}>
                        <Catering />
                      </Suspense>
                    } />
                    <Route path='*' element={<div>404 Not Found</div>} />
                  </Route>

                  {/* Auth Routes (without Layout) */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin/*" element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <Routes>
                          <Route index element={<Dashboard />} />
                          <Route path="orders" element={<Orders />} />
                          <Route path="orders/:orderId" element={<OrderDetails />} />
                          <Route path="edit-data" element={<EditData />} />
                          <Route path="contact-submissions" element={<ContactSubmissions />} />
                          <Route path="catering-requests" element={<CateringSubmissions />} />
                          <Route path="bcc-emails" element={<BCCEmailManagement />} />
                          <Route path="analytics" element={<div>Analytics Coming Soon</div>} />
                          <Route path="data-backup" element={<DataBackup/>}/>
                          <Route path="edit-business-info" element={<EditBusinessInfo />} />
                          <Route path="edit-home-hero" element={<EditHomeHero />} />
                          <Route path="edit-home-reviews" element={<EditHomeReviews />} />
                          <Route path="edit-home-gallery" element={<EditHomeGallery />} />
                          <Route path="edit-home-stats" element={<EditHomeStats />} />
                          <Route path="edit-about" element={<EditAbout />} />
                          <Route path="edit-catering" element={<EditCatering />} />
                          <Route path="edit-notices" element={<EditNotices />} />
                          <Route path="edit-services" element={<EditServices />} />
                        </Routes>
                      </AdminLayout>
                    </ProtectedRoute>
                  } />
                </Routes>
              </Suspense>
            </Router>
          </SiteDataProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;